import {
  IdentityAdministrationContractError,
  type OrganizationInvitationResponse,
} from "@kontextwerk/control-web-client"

export type InvitationPage = Readonly<{
  items: readonly OrganizationInvitationResponse[]
  next_after_id: string | null
}>

const UUID = /^[a-f\d]{8}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{12}$/i
const STATUSES = ["pending", "consumed", "revoked", "expired"] as const
const INVITATION_FIELDS = [
  "id",
  "product_issuer",
  "organization_id",
  "role_id",
  "role_revision",
  "recipient_email",
  "status",
  "created_at",
  "expires_at",
  "issued_by_user_id",
  "issued_by_membership_id",
  "issued_via_delegation_id",
  "consumed_at",
  "consumed_by_user_id",
  "revoked_at",
  "revoked_by_user_id",
  "revoked_via_delegation_id",
] as const

const invalid = (): never => {
  throw new IdentityAdministrationContractError()
}
const fields = (value: unknown, keys: readonly string[]): Record<string, unknown> => {
  if (
    !value ||
    typeof value !== "object" ||
    Array.isArray(value) ||
    Object.keys(value).some((key) => !keys.includes(key))
  )
    return invalid()

  return value as Record<string, unknown>
}
export const uuid = (value: unknown): string =>
  typeof value === "string" && UUID.test(value) ? value.toLowerCase() : invalid()
const nullableUuid = (value: unknown): string | null => (value === null ? null : uuid(value))
export const timestamp = (value: unknown): string =>
  typeof value === "string" &&
  /^\d{4}-\d{2}-\d{2}T.*(?:Z|[+-]\d{2}:\d{2})$/i.test(value) &&
  Number.isFinite(Date.parse(value))
    ? value
    : invalid()
const nullableTimestamp = (value: unknown): string | null =>
  value === null ? null : timestamp(value)
const email = (value: unknown): string =>
  typeof value === "string" && value.length <= 320 && /^[^\s@]+@[^\s@]+$/.test(value)
    ? value
    : invalid()

/** Same closed shape as the published client contract of issued and revoked invitations. */
export const invitation = (value: unknown): OrganizationInvitationResponse => {
  const data = fields(value, INVITATION_FIELDS)
  const role = data.role_revision

  if (
    typeof data.product_issuer !== "string" ||
    !data.product_issuer.trim() ||
    data.product_issuer.length > 120 ||
    !STATUSES.includes(data.status as (typeof STATUSES)[number]) ||
    typeof role !== "number" ||
    !Number.isSafeInteger(role) ||
    role < 1
  )
    return invalid()

  return Object.freeze({
    id: uuid(data.id),
    product_issuer: data.product_issuer,
    organization_id: uuid(data.organization_id),
    role_id: uuid(data.role_id),
    role_revision: role,
    recipient_email: email(data.recipient_email),
    status: data.status as OrganizationInvitationResponse["status"],
    created_at: timestamp(data.created_at),
    expires_at: timestamp(data.expires_at),
    issued_by_user_id: uuid(data.issued_by_user_id),
    issued_by_membership_id: uuid(data.issued_by_membership_id),
    issued_via_delegation_id: nullableUuid(data.issued_via_delegation_id),
    consumed_at: nullableTimestamp(data.consumed_at),
    consumed_by_user_id: nullableUuid(data.consumed_by_user_id),
    revoked_at: nullableTimestamp(data.revoked_at),
    revoked_by_user_id: nullableUuid(data.revoked_by_user_id),
    revoked_via_delegation_id: nullableUuid(data.revoked_via_delegation_id),
  })
}

export const invitationPage = (value: unknown): InvitationPage => {
  const data = fields(value, ["items", "next_after_id"])

  if (!Array.isArray(data.items) || data.items.length > 100) return invalid()

  const items = Object.freeze(data.items.map(invitation))

  if (new Set(items.map((item) => item.id)).size !== items.length) return invalid()

  return Object.freeze({
    items,
    next_after_id: data.next_after_id === null ? null : uuid(data.next_after_id),
  })
}

export const invitationEnvelope = (value: unknown): OrganizationInvitationResponse =>
  invitation(fields(value, ["invitation"]).invitation)
